/**
 * The action registry — the single place that decides what a fleet support
 * action IS: its tier, its label and help copy, whether it needs an emailed
 * authorisation code, and which build phase renders it. The drawer (Task 9)
 * renders from this data with no per-action `if`.
 *
 * `tier`, `needsCode` and `capability` are read straight off `FLEET_ACTIONS`
 * in `@platform/shared-types` rather than restated here — that is the
 * authority on what an action IS, this module only adds the UI-facing
 * label/help/icon/phase. The test suite asserts the two never disagree.
 *
 * `label`/`help` copy is verbatim from the approved mockup
 * (`applications/super-admin-ui/mockups/fleet-device-support.html:162-194`),
 * not reworded here.
 *
 * `impact` (an internal blast-radius probe) and `unlock` (no button in the
 * approved mockup — lock/unlock is a single toggle on the "Lock till" row,
 * not two registry entries) are deliberately excluded from `FLEET_UI_ACTIONS`.
 * The "Reinstall OS" row has no `FLEET_ACTIONS` id at all (it can never run
 * remotely), so it is not a `UiAction` and is exported separately as
 * `DISABLED_REINSTALL_OS` — keeping it out of `FLEET_UI_ACTIONS` is what
 * keeps the shared-types agreement test from indexing `FLEET_ACTIONS` with
 * an id that was never there.
 *
 * PURITY CONTRACT: no network, no React, no AWS, no `Date.now()`. Everything
 * here is a plain object literal, safe to import and call from a test with
 * no mocks. Importing a `lucide-react` icon component is fine — it is a
 * value, not a side effect.
 */

import {
  FileText, Monitor, List, RefreshCw, RotateCw, Lock, ShieldCheck, Download,
  Trash2, AlertCircle, Power, type LucideIcon,
} from 'lucide-react';
import { FLEET_ACTIONS, type FleetTier, type FleetActionId } from '@platform/shared-types';

export interface UiAction {
  id: FleetActionId;
  tier: FleetTier;
  label: string;
  help: string;
  icon: LucideIcon;
  needsCode: boolean;
  capability: string | null;
  /** Gates what renders before later phases land: tier N ships in phase N. */
  availableInPhase: 1 | 2 | 3;
  /**
   * True for an action whose id is registered (another task's registry
   * entry depends on it existing) but whose DELIVERY is not deployed yet —
   * `ActionTiers` renders it disabled with a title saying so, distinct from
   * phase gating. `update_now`'s executor ships in Task 12; until then a
   * click here can only ever fail, and a live button whose only outcome is
   * a failed command is worse than no button.
   */
  deliveryPending?: boolean;
}

/** Tier 1 actions render in phase 1, tier 2 in phase 2, tier 3 in phase 3. */
export const CURRENT_PHASE: 1 | 2 | 3 = 3;

/**
 * FAILS LOUDLY BUT SURVIVABLY. `FLEET_ACTIONS[id].tier` on an id the shared
 * registry does not carry threw `Cannot read properties of undefined` at
 * MODULE scope, which took the whole Fleet page down — hit live via a stale
 * Vite dep cache serving an older `@platform/shared-types`. One unknown id
 * now costs one button and a named console error, not the page. It is never
 * silently swallowed: the message says exactly which id is missing and why.
 */
const ui = (
  id: FleetActionId, label: string, help: string, icon: LucideIcon,
  opts: { deliveryPending?: boolean } = {},
): UiAction | null => {
  const meta = FLEET_ACTIONS?.[id];
  if (!meta) {
    console.error(
      `[fleet] actionRegistry: no FLEET_ACTIONS entry for "${id}" — this UI registry and ` +
      '@platform/shared-types have drifted (rebuild shared-types and clear node_modules/.vite). ' +
      'That action is omitted from the support drawer; every other action still renders.',
    );
    return null;
  }
  return {
    id,
    tier: meta.tier,
    label,
    help,
    icon,
    needsCode: meta.needsCode,
    capability: meta.capability,
    availableInPhase: meta.tier,
    deliveryPending: opts.deliveryPending,
  };
};

/** One entry per user-facing fleet support action, ordered by what it can destroy. */
export const FLEET_UI_ACTIONS: UiAction[] = ([
  // Tier 1 — diagnostics, reads only.
  ui('collect_logs', 'Collect logs',
    'Last 30 min of app + device log, uploaded and held 7 days', FileText),
  ui('capture_screen', 'Capture screen',
    'What the cashier is looking at, both screens', Monitor),
  ui('device_report', 'Device report',
    'Peripherals, USB bus, displays, storage, versions', List),
  ui('force_resync', 'Force re-sync',
    'Drain the sale outbox and re-pull config now', RefreshCw),

  // Tier 2 — interventions, reversible.
  ui('restart', 'Restart till',
    'About a minute. Back to the sign-in screen', RotateCw),
  ui('lock', 'Lock till',
    'Freeze the screen with a message. Reversible from here', Lock),
  ui('apply_policy', 'Apply lockdown policy',
    'ADB off · OEM unlock off · safe boot off', ShieldCheck),
  ui('push_version', 'Push app version',
    'Install a specific build now, ignoring the ring', Download),
  // Task 12 delivered the executor (device-side arm via UpdateManager) and
  // the server-side resolveTarget()-backed dispatch — live, no longer
  // deliveryPending.
  ui('update_now', 'Update now',
    'Take the build this device is already targeted at, right now', Download),

  // Tier 3 — destructive, cannot be undone.
  ui('clear_pairing', 'Clear pairing',
    'Unpairs the register. OS and data stay. Re-provision needed', Trash2),
  ui('factory_wipe', 'Factory wipe',
    'Erases everything. The unit comes back as new hardware', AlertCircle),
] as (UiAction | null)[]).filter((a): a is UiAction => a !== null);

/**
 * The mockup's permanently-disabled "Reinstall OS" row. It has no
 * `FLEET_ACTIONS` id — it can never run remotely — so it is not a
 * `UiAction` and is never in `FLEET_UI_ACTIONS`/`actionsByTier`. The drawer
 * renders it as a fourth, always-disabled tier-3 row alongside
 * `actionsByTier(3)`.
 */
export const DISABLED_REINSTALL_OS = {
  label: 'Reinstall OS',
  help: 'Not possible remotely — SP Flash Tool, physical only',
  icon: Power,
  tier: 3 as const,
} as const;

export function actionsByTier(tier: FleetTier): UiAction[] {
  return FLEET_UI_ACTIONS.filter((a) => a.tier === tier);
}

export const TIER_META = {
  1: { label: 'Tier 1', title: 'Diagnostics — reads only',
       why: 'Runs on a click. Nothing here can change or break a till.',
       pill: 'bg-emerald-500/15 text-emerald-600' },
  2: { label: 'Tier 2', title: 'Interventions — reversible',
       why: 'One confirmation. Changes the till, but nothing is lost.',
       pill: 'bg-amber-500/15 text-amber-600' },
  3: { label: 'Tier 3', title: 'Destructive — cannot be undone',
       why: 'Typed confirmation + a reason. Always audited.',
       pill: 'bg-rose-500/15 text-rose-600' },
} as const;
